import { useSearchParams } from 'react-router-dom';

type Defaults<F extends Record<string, string>> = { pageSize: number; filters: F };

/**
 * Page, page size and filters live in the URL so lists survive refresh, back and sharing.
 * Any filter change resets the page to 1.
 */
export function useListUrlState<F extends Record<string, string>>(defaults: Defaults<F>) {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const pageSize = Number(searchParams.get('pageSize')) || defaults.pageSize;
  const filters = Object.fromEntries(
    Object.entries(defaults.filters).map(([key, fallback]) => [key, searchParams.get(key) ?? fallback]),
  ) as F;

  const update = (patch: Record<string, string | number>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
          if (value === '' || value === defaults.filters[key]) next.delete(key);
          else next.set(key, String(value));
        }
        if (next.get('page') === '1') next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  return {
    page,
    pageSize,
    filters,
    setPage: (p: number) => { update({ page: p }); },
    setPageSize: (size: number) => { update({ pageSize: size, page: 1 }); },
    setFilters: (patch: Partial<F>) => { update({ ...(patch as Record<string, string>), page: 1 }); },
    /** Flat params for the API call and the query key. */
    params: { page, pageSize, ...filters },
  };
}
